import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { fetchMessages } from '../../services/api';
import Spinner from '../ui/Spinner';
import ErrorMessage from '../ui/ErrorMessage';

/**
 * Props for the MessagesPanel component.
 */
interface MessagesPanelProps {
  nodeId?: number;
  limit?: number;
}

/**
 * MessagesPanel component.
 * Displays recent messages from the mesh network.
 */
const MessagesPanel: React.FC<MessagesPanelProps> = ({ nodeId, limit = 15 }) => {
  // Fetch messages data
  const { 
    data: messages, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useQuery({
    queryKey: nodeId ? ['messages', nodeId] : ['messages'],
    queryFn: () => fetchMessages(limit, nodeId),
    // Remove refetchInterval to avoid double refreshing
    staleTime: Infinity, // Only refresh when explicitly invalidated
  });

  // Format the timestamp
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format the date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <Spinner size="md" />
          <LoadingText>Loading messages...</LoadingText>
        </LoadingContainer>
      );
    }

    if (isError) {
      return (
        <ErrorMessage 
          message="Failed to load messages" 
          details={error?.toString()}
          onRetry={() => refetch()}
        />
      );
    }

    if (!messages || messages.length === 0) {
      return (
        <EmptyState>
          <EmptyText>No messages found</EmptyText>
        </EmptyState>
      );
    }

    return (
      <MessagesContainer>
        {messages.map((message) => (
          <MessageItem key={message.id} $isChannel1={message.channelId === 1 || message.channelId === '1'}>
            <MessageHeader>
              <MessageSender>
                <SenderLabel>From:</SenderLabel> {message.senderName}
              </MessageSender>
              <MessageTime>
                {formatTime(message.timestamp)}
              </MessageTime>
            </MessageHeader>
            <MessageRecipient>
              <RecipientLabel>To:</RecipientLabel> {message.receiverName}
            </MessageRecipient>
            <MessageContent $isChannel1={message.channelId === 1 || message.channelId === '1'}>
              {message.content}
            </MessageContent>
            <MessageFooter>
              <MessageType $isChannel1={message.channelId === 1 || message.channelId === '1'}>
                {message.channelId === 1 || message.channelId === '1' 
                  ? 'NHMesh' 
                  : message.channelId === 0 || message.channelId === '0'
                    ? 'Public'
                    : message.type}
              </MessageType>
              <MessageDate>{formatDate(message.timestamp)}</MessageDate>
            </MessageFooter>
          </MessageItem>
        ))}
      </MessagesContainer>
    );
  };

  return (
    <MessagesPanelContainer>
      <PanelHeader>
        <Title>Recent Messages</Title>
      </PanelHeader>
      {renderContent()}
    </MessagesPanelContainer>
  );
};

const MessagesPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  height: 100%;
  min-height: 0; /* Critical for proper flex behavior */
`;

const PanelHeader = styled.div`
  padding: 4px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  flex-shrink: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.lg};
  gap: ${({ theme }) => theme.space.md};
  flex: 1;
`;

const LoadingText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.lg};
  flex: 1;
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textLight};
`;

const MessagesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 4px;
  min-height: 0; /* Critical for proper flex behavior */
  
  /* Add some spacing between messages */
  & > * + * {
    margin-top: 4px;
  }
`;

interface MessageItemProps {
  $isChannel1?: boolean;
}

const MessageItem = styled.div<MessageItemProps>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space.sm};
  background-color: ${({ theme, $isChannel1 }) => 
    $isChannel1 
      ? '#2b3c5a' // Blueish background for channel 1
      : theme.colors.backgroundDark
  };
  border-radius: ${({ theme }) => theme.radii.md};
  flex-shrink: 0; /* Prevent shrinking */
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const MessageSender = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SenderLabel = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  opacity: 0.8;
`;

const MessageTime = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textLight};
`;

const MessageRecipient = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: ${({ theme }) => theme.space.xs};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RecipientLabel = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  opacity: 0.8;
`;

interface MessageContentProps {
  $isChannel1?: boolean;
}

const MessageContent = styled.div<MessageContentProps>`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme, $isChannel1 }) => $isChannel1 ? 'white' : theme.colors.text};
  margin-bottom: ${({ theme }) => theme.space.xs};
  word-break: break-word;
`;

const MessageFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface MessageTypeProps {
  $isChannel1?: boolean;
}

const MessageType = styled.div<MessageTypeProps>`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ $isChannel1 }) => $isChannel1 ? 'white' : 'inherit'};
  background-color: ${({ theme, $isChannel1 }) => 
    $isChannel1 
      ? '#1a2c4a' // Darker blue for the pill on channel 1 
      : theme.colors.backgroundLight
  };
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const MessageDate = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textLight};
`;

export default MessagesPanel;